import { useEffect, useState } from "react";
import { api, formatDate, formatINR, formatApiError } from "../lib/api";
import { PageHeader, StatusBadge, Empty } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

export default function RFQInbox() {
  const [rfqs, setRfqs] = useState(null);
  const [open, setOpen] = useState(null);
  const [price, setPrice] = useState("");
  const [days, setDays] = useState("");
  const [notes, setNotes] = useState("");
  const [vendorId, setVendorId] = useState(null);

  const loadInbox = async () => {
    try {
      const [r, v] = await Promise.all([api.get("/rfqs"), api.get("/vendors/me")]);
      setRfqs(r.data || []);
      setVendorId(v.data?.id || null);
    } catch {
      setRfqs([]);
    }
  };

  useEffect(() => { loadInbox(); }, []);

  const existingResponse = (r) => {
    if (!vendorId || !r) return null;
    return (r.responses || []).find((x) => x.vendor_id === vendorId) || null;
  };

  const statusFor = (r) => {
    if (!vendorId) return r.status;
    const has = existingResponse(r);
    if (!has) return "New";
    if (r.status === "Closed") return "Closed";
    return "Responded";
  };

  const openRfq = (r) => {
    const existing = existingResponse(r);
    setPrice(existing?.price != null ? String(existing.price) : "");
    setDays(existing?.timeline_days != null ? String(existing.timeline_days) : "");
    setNotes(existing?.notes || "");
    setOpen(r);
  };

  const submit = async () => {
    if (!price || !days || !notes) { toast.error("Fill all fields"); return; }
    try {
      await api.post(`/rfqs/${open.id}/respond`, { price: parseFloat(price), timeline_days: parseInt(days), notes });
      toast.success("Response submitted");
      setPrice(""); setDays(""); setNotes(""); setOpen(null);
      loadInbox();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not submit"); }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader title="RFQ Inbox" subtitle="Respond to incoming RFQs from buyers" />

      {rfqs === null && <Skeleton className="h-48" />}
      {rfqs && rfqs.length === 0 && <Empty title="Inbox is empty" subtitle="RFQs from buyers will appear here" />}

      <div className="space-y-3">
        {(rfqs || []).map((r) => (
          <button type="button" key={r.id} onClick={(e) => { e.preventDefault(); e.stopPropagation(); const item = r; setTimeout(() => openRfq(item), 0); }} className="w-full text-left bg-white border border-zinc-200 hover:border-[#6366F1] rounded-md p-5 transition-colors" data-testid="inbox-rfq">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold">{r.title}</div>
                <div className="text-sm text-zinc-600 mt-1 line-clamp-2">{r.description}</div>
                <div className="text-xs text-zinc-500 mt-2">{r.buyer_name} · {r.category} · Deadline {formatDate(r.deadline)}</div>
              </div>
              <StatusBadge status={statusFor(r)} />
            </div>
          </button>
        ))}
      </div>

      <Sheet open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {open && (
            <>
              <SheetHeader>
                <SheetTitle>{open.title}</SheetTitle>
                <SheetDescription>Review the request and submit your quote.</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Buyer</div>
                  <div className="text-sm font-medium">{open.buyer_name}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Description</div>
                  <p className="text-sm text-zinc-700">{open.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><div className="text-xs text-zinc-500">Category</div><div className="text-sm font-medium">{open.category}</div></div>
                  <div><div className="text-xs text-zinc-500">Deadline</div><div className="text-sm font-medium">{formatDate(open.deadline)}</div></div>
                </div>

                {existingResponse(open) && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
                    Previous quote: {formatINR(existingResponse(open).price)} · {existingResponse(open).timeline_days} days
                    <div className="text-xs text-zinc-600 mt-1">Submitting again will replace your response.</div>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-sm mb-3">Submit your response</h4>
                  <div className="space-y-3">
                    <div><Label>Price quote (₹)</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" data-testid="response-price" /></div>
                    <div><Label>Timeline (days)</Label><Input type="number" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1.5" data-testid="response-days" /></div>
                    <div><Label>Proposal notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="mt-1.5" data-testid="response-notes" /></div>
                    <Button type="button" onClick={submit} className="w-full bg-[#6366F1] hover:bg-[#4F46E5]" data-testid="submit-response">Submit Response</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
